/**
 * Admin → Skills. The credential catalogue for this tenant. Lives on
 * its own page (sibling of Members) so admins can browse and edit
 * skill names independently of the team list — previously the two
 * sections stacked on /members and the catalogue was easy to miss.
 *
 * Skills are referenced by:
 *   - service_staff_slots (a service requires staff with skill X)
 *   - staff_skills (a member holds skill X)
 * Archived skills stay referenced by past bookings but are hidden
 * from new assignments.
 */
import { createFileRoute } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Alert, AlertDescription, AlertTitle } from '@bookaburra/ui';
import { fetchCapabilities } from '../booking-flow/capabilities-api';
import { fetchBookingTenant } from '../booking-flow/api';
import { useDocumentTitle } from '../lib/use-document-title';
import { SkillsCard } from '../components/admin/capabilities-sections';

export const Route = createFileRoute('/admin/$slug/skills')({
  component: SkillsPage,
});

function SkillsPage() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();

  const capsQ = useQuery({
    queryKey: ['admin-capabilities', slug],
    queryFn: () => fetchCapabilities(slug),
  });
  const tenantQ = useQuery({
    queryKey: ['booking-tenant', slug],
    queryFn: () => fetchBookingTenant(slug),
    staleTime: 60_000,
  });
  useDocumentTitle(
    tenantQ.data ? `Skills · ${tenantQ.data.tenant.name} Admin` : 'Skills',
  );

  function invalidateCaps() {
    qc.invalidateQueries({ queryKey: ['admin-capabilities', slug] });
  }

  return (
    <main className="mx-auto max-w-4xl px-6 py-10 sm:px-12">
      {capsQ.error ? (
        <Alert variant="destructive">
          <AlertTitle>Couldn&rsquo;t load skills</AlertTitle>
          <AlertDescription>{(capsQ.error as Error).message}</AlertDescription>
        </Alert>
      ) : null}

      {capsQ.data ? (
        <SkillsCard
          slug={slug}
          skills={capsQ.data.skills}
          onChange={invalidateCaps}
        />
      ) : null}
    </main>
  );
}
